import axios from "axios";

const useSubscribeFormBlock = (blockElement: HTMLElement): void => {
    const formElement = blockElement.querySelector(".subscribe-form-block__form") as HTMLFormElement;
    const emailInputElement = blockElement.querySelector('.subscribe-form-block__email-input') as HTMLInputElement;
    const consentInputElement = blockElement.querySelector('.subscribe-form-block__consent-checkbox-input') as HTMLInputElement;
    const subscribeUrl: string | undefined = formElement.dataset.subscribeUrl;
    const subscriptionLanguage = formElement.dataset.subscribeLanguage || "sv";
    const statusMessageElement = formElement.querySelector('.subscribe-form-block__status-message') as HTMLParagraphElement;
    
    const validOnSubmit = (event: SubmitEvent): boolean => {
        let isEmailInputValid = isInputValid(emailInputElement, event);
        let isConsentInputValid = isInputValid(consentInputElement, event);
        
        if (!isEmailInputValid || !isConsentInputValid) {
            return false;
        } else {
            return true;
        }
    }
    
    const isInputValid = (inputElement: HTMLInputElement, event: Event): boolean => {
        let inputContainerElement = inputElement.closest('.form-field-group') as HTMLDivElement;
        let inputElementClass: string | undefined = undefined;
        let errorMessageElement =
            inputContainerElement.querySelector('.subscribe-form-block__validation-message') as HTMLParagraphElement;

        if (inputElement.type === 'email') {
            inputElementClass = 'text-field';
        } else {
            inputElementClass = 'check-field__input';
        }

        if (event instanceof SubmitEvent) {
            if (inputElement.checkValidity()) {
                setFormFieldAsValid(inputElement, inputElementClass, errorMessageElement);

                return true;
            } else {
                setFormFieldAsInvalid(inputElement, inputElementClass, errorMessageElement);
                setFocusToFirstInvalidInput();

                return false;
            }
        } else {
            if (inputElement.classList.contains(`${inputElementClass}--valid`) || inputElement.classList.contains(`${inputElementClass}--invalid`)) {
                if (inputElement.checkValidity()) {
                    setFormFieldAsValid(inputElement, inputElementClass, errorMessageElement);

                    return true;
                } else {
                    setFormFieldAsInvalid(inputElement, inputElementClass, errorMessageElement);

                    return false;
                }
            } else {
                if (inputElement.checkValidity()) {
                    setFormFieldAsValid(inputElement, inputElementClass, errorMessageElement);
                    return true;
                } else {
                    return false;
                }
            }
        }
    }
    
    const setFormFieldAsValid = (
        inputElement: HTMLInputElement,
        inputElementClass: string,
        errorMessageElement: HTMLParagraphElement
    ): void => {
        if (inputElement.type === 'email') {
            inputElement.classList.add(`${inputElementClass}--valid`);
        }
        
        if (inputElement.classList.contains(`${inputElementClass}--invalid`)) {
            inputElement.classList.remove(`${inputElementClass}--invalid`);
        }

        if (inputElement.hasAttribute('aria-invalid')) {
            inputElement.removeAttribute('aria-invalid');
        }

        errorMessageElement.innerText = "";
        errorMessageElement.hidden = true;
    }
    
    const setFormFieldAsInvalid = (
        inputElement: HTMLInputElement,
        inputElementClass: string,
        errorMessageElement: HTMLParagraphElement
    ): void => {
        const errorMessage = inputElement.dataset.validationMessage as string;

        inputElement.classList.add(`${inputElementClass}--invalid`);

        if (inputElement.classList.contains(`${inputElementClass}--valid`)) {
            inputElement.classList.remove(`${inputElementClass}--valid`);
        }

        inputElement.setAttribute('aria-invalid', 'true');
        inputElement.setAttribute('aria-describedby', `${errorMessageElement.id}`);

        errorMessageElement.innerText = errorMessage;
        errorMessageElement.hidden = false;
    }
    
    const clearFormInputsOnSuccess = (): void => {
        emailInputElement.value = "";
        emailInputElement.classList.remove('text-field--valid');
        
        consentInputElement.checked = false;
        consentInputElement.classList.remove('check-field__input--valid');
    }

    const setFocusToFirstInvalidInput = () => {
        const invalidInputElements = Array.from(formElement.querySelectorAll('[class*=\'--invalid\']')) as HTMLInputElement[];

        if (invalidInputElements.length > 0) {
            const firstElement = invalidInputElements[0];
            firstElement.focus();
        }
    }

    const onSubmit = ():void => {
        if (subscribeUrl !== undefined) {
            axios.post(subscribeUrl, {
                email: emailInputElement.value,
                language: subscriptionLanguage
            }, {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded"
                }
            })
                .then((response) => {
                    statusMessageElement.textContent = response.data.message;
                    statusMessageElement.hidden = false;
    
                    if (response.data.success !== null) {
                        if (response.data.success) {
                            statusMessageElement.classList.add("status-message--success");
                            clearFormInputsOnSuccess();
                        } else {
                            statusMessageElement.classList.add("status-message--error");
                        }
                    } else {
                        statusMessageElement.classList.add("status-message--error");
                    }
                })
                .catch((error: Error) => {
                    statusMessageElement.classList.add("status-message--error");
                });
        }
    }

    /* Initialize and add event listeners */
    const initialize = (): void => {
        emailInputElement.addEventListener('input', (event: Event) => {
            if (!isInputValid(emailInputElement, event)) {
                return;
            }
        });

        emailInputElement.addEventListener('blur', (event: Event) => {
            if (!isInputValid(emailInputElement, event)) {
                return;
            }
        });
        
        consentInputElement.addEventListener('input', (event: Event) => {
            if (!isInputValid(consentInputElement, event)) {
                return;
            }
        });
        
        consentInputElement.addEventListener('blur', (event: Event) => {
            if (!isInputValid(consentInputElement, event)) {
                return;
            }
        });
        
        formElement.addEventListener('submit', (event: SubmitEvent) => {
            event.preventDefault();

            if (!validOnSubmit(event)) {
                return;
            }
            
            onSubmit();
        });
    };

    initialize();
};

document.addEventListener('DOMContentLoaded', () => {
    const subscribeFormBlockElements = document.querySelectorAll('.subscribe-form-block') as NodeListOf<HTMLDivElement>;

    if (subscribeFormBlockElements.length > 0) {
        subscribeFormBlockElements.forEach((blockElement) => {
            useSubscribeFormBlock(blockElement);
        })
    }
});
